import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export type LicenceClass = 'G2' | 'G'

export interface CourseTitleSummary {
  courseId: string
  title: string
  licenceClass: LicenceClass

  // The course's current LIVE content — the highest-numbered published version. Left
  // published forever once superseded (never archived on replace) so a student already
  // assigned against it keeps read access; see useFlatCurriculum's own module comment.
  publishedVersionId: string | null
  publishedVersionNumber: number | null
  pdfAssetId: string | null
  pdfTitle: string | null
  videoAssetId: string | null
  videoTitle: string | null

  // A working draft in progress (brand-new course not yet published, or a replace
  // started via startReplace() and not yet published). At most one at a time.
  draftVersionId: string | null
  draftLessonId: string | null
  draftPdfAssetId: string | null
  draftVideoAssetId: string | null
}

// A course always has at least a draft the moment it's created (createCourseTitle
// creates v1 as draft immediately) — so a course row is in exactly one of three states:
// draft only (never published yet), published only (no replace in progress), or both
// (a replace is in progress). Never neither.
//
// "Replace" never archives the version it supersedes (see the plan's own Context section
// — archiving would break read access for a student already assigned against the old
// lesson, since migration 038's RLS for an already-enrolled student requires the
// version to still be 'published'). Every surface that offers NEW assignments
// (useCourseTitles here, the instructor Library, the Enroll/assign pickers in
// useCurriculumAssignments.ts) instead always picks the highest version_number among
// published versions, so old content stays reachable for whoever already has it without
// ever being offered again going forward.
export function useCourseTitles(licenceClass: LicenceClass) {
  const [courses, setCourses] = useState<CourseTitleSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchCourses = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data, error: fetchErr } = await supabase
      .from('curriculum_courses')
      .select(`
        id, title, licence_class,
        curriculum_course_versions (
          id, version_number, status,
          curriculum_version_modules (
            id,
            curriculum_version_lessons (
              id,
              curriculum_lesson_assets (id, asset_type, title, status)
            )
          )
        )
      `)
      .eq('licence_class', licenceClass)
      .eq('status', 'active')
      .order('created_at', { ascending: false })

    if (fetchErr) { setError('Could not load courses.'); setLoading(false); return }

    const summaries: CourseTitleSummary[] = (data ?? []).map((c: any) => {
      const versions = (c.curriculum_course_versions ?? []) as any[]
      const published = versions
        .filter(v => v.status === 'published')
        .sort((a, b) => b.version_number - a.version_number)[0]
      const draft = versions
        .filter(v => v.status === 'draft')
        .sort((a, b) => b.version_number - a.version_number)[0]

      const pick = (v: any | undefined) => {
        if (!v) return { versionId: null, versionNumber: null, lessonId: null, pdfAssetId: null, pdfTitle: null, videoAssetId: null, videoTitle: null }
        const mod = (v.curriculum_version_modules ?? [])[0]
        const lesson = (mod?.curriculum_version_lessons ?? [])[0]
        const assets = (lesson?.curriculum_lesson_assets ?? []).filter((a: any) => a.status === 'active')
        const pdf = assets.find((a: any) => a.asset_type === 'pdf')
        const video = assets.find((a: any) => a.asset_type === 'video')
        return {
          versionId: v.id as string, versionNumber: v.version_number as number, lessonId: lesson?.id ?? null,
          pdfAssetId: pdf?.id ?? null, pdfTitle: pdf?.title ?? null,
          videoAssetId: video?.id ?? null, videoTitle: video?.title ?? null,
        }
      }

      const pub = pick(published)
      const drf = pick(draft)

      return {
        courseId: c.id, title: c.title, licenceClass: c.licence_class,
        publishedVersionId: pub.versionId, publishedVersionNumber: pub.versionNumber,
        pdfAssetId: pub.pdfAssetId, pdfTitle: pub.pdfTitle,
        videoAssetId: pub.videoAssetId, videoTitle: pub.videoTitle,
        draftVersionId: drf.versionId, draftLessonId: drf.lessonId,
        draftPdfAssetId: drf.pdfAssetId, draftVideoAssetId: drf.videoAssetId,
      }
    })

    setCourses(summaries)
    setLoading(false)
  }, [licenceClass])

  useEffect(() => { fetchCourses() }, [fetchCourses])

  return { courses, loading, error, refresh: fetchCourses }
}

// Finds the version's single module/lesson (this flat UI only ever creates one of each
// per version) or creates whichever is missing — idempotent, so it's safe to call against
// a version that already has a module/lesson from the old deep-hierarchy admin UI (the
// two courses created before this change), not just ones this orchestration created
// itself. Takes the first module/lesson found rather than matching by title, so it's
// agnostic to whatever the admin actually named them through the old UI.
async function ensureModuleAndLesson(
  versionId: string,
  lessonTitle: string
): Promise<{ moduleId: string; lessonId: string } | { error: string }> {
  const { data: modules } = await supabase
    .from('curriculum_version_modules')
    .select('id')
    .eq('course_version_id', versionId)
    .order('sort_order', { ascending: true })
    .limit(1)
  let moduleId = modules?.[0]?.id as string | undefined
  if (!moduleId) {
    const { data, error } = await supabase.rpc('create_curriculum_module', {
      p_course_version_id: versionId, p_title: 'Content', p_description: undefined,
    })
    if (error) return { error: error.message }
    moduleId = data as string
  }

  const { data: lessons } = await supabase
    .from('curriculum_version_lessons')
    .select('id')
    .eq('module_id', moduleId)
    .order('sort_order', { ascending: true })
    .limit(1)
  let lessonId = lessons?.[0]?.id as string | undefined
  if (!lessonId) {
    const { data, error } = await supabase.rpc('create_curriculum_lesson', {
      p_module_id: moduleId, p_title: lessonTitle, p_description: undefined,
      p_estimated_minutes: undefined, p_learning_objectives: undefined,
    })
    if (error) return { error: error.message }
    lessonId = data as string
  }

  return { moduleId, lessonId }
}

// Carries an existing active asset forward into a new (draft) lesson without re-uploading
// bytes: reserve a path on the new lesson via the normal two-phase draft RPC, copy the
// old Storage object directly to that reserved path (server-side, no download+re-upload
// round trip — the existing "admin read all" + "admin upload to reserved path" storage
// policies from migration 064 already permit both halves of this for drivetrack_admin),
// then finalize exactly like a real upload would.
async function carryForwardAsset(oldAssetId: string, newLessonId: string): Promise<{ ok: true } | { ok: false; error: string }> {
  const { data: oldAsset, error: oldErr } = await supabase
    .from('curriculum_lesson_assets')
    .select('asset_type, title, storage_path')
    .eq('id', oldAssetId)
    .single()
  if (oldErr || !oldAsset) return { ok: false, error: oldErr?.message ?? 'source_asset_not_found' }

  const { data: draft, error: draftErr } = await supabase.rpc('create_curriculum_lesson_asset_draft', {
    p_lesson_id: newLessonId, p_asset_type: oldAsset.asset_type, p_title: oldAsset.title,
  })
  if (draftErr) return { ok: false, error: draftErr.message }
  const row = Array.isArray(draft) ? draft[0] : draft
  const newAssetId: string = row?.asset_id
  const newStoragePath: string = row?.storage_path

  const { error: copyErr } = await supabase.storage
    .from('curriculum-assets')
    .copy(oldAsset.storage_path as string, newStoragePath)
  if (copyErr) return { ok: false, error: copyErr.message }

  const { error: finalizeErr } = await supabase.rpc('finalize_curriculum_lesson_asset', { p_asset_id: newAssetId })
  if (finalizeErr) return { ok: false, error: finalizeErr.message }

  return { ok: true }
}

export function useFlatCurriculumActions() {
  const [working, setWorking] = useState(false)

  // For a draft version that ALREADY exists but has no lesson yet — e.g. a course
  // created before this flat UI existed, whose original draft never got a module (the
  // old UI's "+ Add module" button had a bug — fixed, but left some courses with an
  // empty draft behind). Reuses that same draft rather than treating "no lesson" as "no
  // draft" and forking a whole new version — that confusion was the actual bug that
  // caused uploads to scatter across several stray draft versions.
  const ensureDraftLesson = useCallback(async (versionId: string, title: string) => {
    setWorking(true)
    try {
      const ml = await ensureModuleAndLesson(versionId, title)
      if ('error' in ml) return { ok: false, error: ml.error }
      return { ok: true, lessonId: ml.lessonId }
    } finally {
      setWorking(false)
    }
  }, [])

  const createCourseTitle = useCallback(async (licenceClass: LicenceClass, title: string) => {
    setWorking(true)
    try {
      const { data: courseId, error: courseErr } = await supabase.rpc('create_curriculum_course', {
        p_title: title, p_licence_class: licenceClass, p_description: undefined,
      })
      if (courseErr) return { ok: false, error: courseErr.message }

      const { data: versionRows, error: versionErr } = await supabase.rpc('create_curriculum_course_version', {
        p_course_id: courseId, p_title: 'v1', p_description: undefined,
      })
      if (versionErr) return { ok: false, error: versionErr.message }
      const versionId = (Array.isArray(versionRows) ? versionRows[0] : versionRows)?.version_id as string

      const ml = await ensureModuleAndLesson(versionId, title)
      if ('error' in ml) return { ok: false, error: ml.error }

      return { ok: true, courseId: courseId as string, versionId, lessonId: ml.lessonId }
    } finally {
      setWorking(false)
    }
  }, [])

  // Forks the course's current published version into a new draft, carrying both
  // existing assets forward unchanged. The admin then re-uploads whichever file actually
  // changed (via uploadAsset below, passing the carried-forward asset id to replace).
  const startReplace = useCallback(async (
    courseId: string,
    title: string,
    current: { pdfAssetId: string | null; videoAssetId: string | null }
  ) => {
    setWorking(true)
    try {
      const { data: versionRows, error: versionErr } = await supabase.rpc('create_curriculum_course_version', {
        p_course_id: courseId, p_title: `v${Date.now()}`, p_description: undefined,
      })
      if (versionErr) return { ok: false, error: versionErr.message }
      const newVersionId = (Array.isArray(versionRows) ? versionRows[0] : versionRows)?.version_id as string

      const ml = await ensureModuleAndLesson(newVersionId, title)
      if ('error' in ml) return { ok: false, error: ml.error }
      const newLessonId = ml.lessonId

      for (const assetId of [current.pdfAssetId, current.videoAssetId]) {
        if (!assetId) continue
        const copied = await carryForwardAsset(assetId, newLessonId)
        if (!copied.ok) return { ok: false, error: copied.error }
      }

      return { ok: true, versionId: newVersionId, lessonId: newLessonId }
    } finally {
      setWorking(false)
    }
  }, [])

  // Uploads into a draft lesson. If replacingAssetId is given (an existing active asset
  // of the same type already in this draft — either carried forward by startReplace, or
  // left over from an earlier upload in this same draft session), it's archived first;
  // archive_curriculum_lesson_asset only ever permits this on a draft version (migration
  // 066), which this always is here.
  const uploadAsset = useCallback(async (
    lessonId: string, assetType: 'pdf' | 'video', file: File, title: string, replacingAssetId: string | null
  ) => {
    setWorking(true)
    try {
      if (replacingAssetId) {
        const { data, error: archiveErr } = await supabase.rpc('archive_curriculum_lesson_asset', { p_asset_id: replacingAssetId })
        const result = data as { ok: boolean; code?: string } | null
        if (archiveErr || !result?.ok) return { ok: false, error: result?.code ?? archiveErr?.message }
      }

      const { data: draft, error: draftErr } = await supabase.rpc('create_curriculum_lesson_asset_draft', {
        p_lesson_id: lessonId, p_asset_type: assetType, p_title: title,
      })
      if (draftErr) return { ok: false, error: draftErr.message }
      const row = Array.isArray(draft) ? draft[0] : draft
      const assetId: string = row?.asset_id
      const storagePath: string = row?.storage_path

      const { error: uploadErr } = await supabase.storage
        .from('curriculum-assets')
        .upload(storagePath, file, { contentType: file.type, upsert: false })
      if (uploadErr) return { ok: false, error: uploadErr.message }

      const { error: finalizeErr } = await supabase.rpc('finalize_curriculum_lesson_asset', { p_asset_id: assetId })
      if (finalizeErr) return { ok: false, error: finalizeErr.message }

      return { ok: true, assetId }
    } finally {
      setWorking(false)
    }
  }, [])

  const publishDraft = useCallback(async (versionId: string) => {
    setWorking(true)
    try {
      const { error: rpcErr } = await supabase.rpc('publish_course_version', { p_version_id: versionId })
      if (rpcErr) return { ok: false, error: rpcErr.message }
      return { ok: true }
    } finally {
      setWorking(false)
    }
  }, [])

  return { working, createCourseTitle, ensureDraftLesson, startReplace, uploadAsset, publishDraft }
}
